import * as path from "node:path";

import { CliError, EXIT_INPUT, EXIT_OK, type CommandOutput } from "../../common.js";
import { emptyContext, type RunContext } from "../../domain/ingest/video-context.js";
import { emptyV4IdentityMap, type V4IdentityMap } from "../../domain/script/v4/from-v3.js";
import { withIngestWorkspaceLock } from "../../infra/ingest/workspace-lock.js";
import { WorkspaceStore } from "../../infra/ingest/workspace-store.js";
import { parseIngestErrorSidecar } from "./failure.js";
import { deriveIngestStatus } from "./status.js";
import {
  deriveVideoTitle,
  migrateVideoManifest,
  publishFinalizedVideoWorkspace,
  readCurrentVideoManifest,
} from "./video-pipeline.js";
import {
  assertVideoSkipSources,
  readVideoSkipLedger,
  reconcileVideoSkips,
  writeVideoSkipLedger,
  type VideoSkipRecord,
} from "./video-skip-state.js";

type Dict = Record<string, unknown>;

function inputError(message: string, code: string, hint: string, actual: string[]): CliError {
  return new CliError(message, {
    exitCode: EXIT_INPUT,
    errorCode: code,
    hint,
    data: { actual },
  });
}

function episodeOf(value: unknown): number {
  const episode = Number(value);
  if (!Number.isInteger(episode) || episode <= 0) {
    throw inputError("--episode must be a positive integer.", "INGEST_SKIP_EPISODE_INVALID", "Pass the current next_episode from `scriptctl ingest status`.", [String(value)]);
  }
  return episode;
}

async function skipVideoEpisodeUnlocked(workspacePath: string, episode: number, reason: string): Promise<boolean> {
  const store = new WorkspaceStore(workspacePath);
  if (!store.has("manifest.json")) {
    throw inputError("The ingest workspace does not exist.", "INGEST_SKIP_WORKSPACE_MISSING", "Run video ingest first, then retry the failed episode or skip it.", [workspacePath]);
  }
  const header = store.readJson<{ source?: { kind?: string } }>("manifest.json");
  if (header.source?.kind === "text") {
    throw inputError("Only video ingest episodes can be skipped.", "INGEST_SKIP_WRONG_PIPELINE", "Use skip only with a video ingest workspace.", [workspacePath]);
  }
  const manifest = readCurrentVideoManifest(store);
  if (!manifest) {
    throw inputError("The video ingest manifest is missing.", "INGEST_SKIP_WORKSPACE_MISSING", "Run video ingest first.", [workspacePath]);
  }
  const sourceEpisode = manifest.episodes.find((candidate) => candidate.episode === episode);
  if (!sourceEpisode) {
    throw inputError("The requested episode is not in the video source.", "INGEST_SKIP_EPISODE_NOT_FOUND", "Pass an episode listed in manifest.json.", [`episode=${episode}`]);
  }

  const ledger = readVideoSkipLedger(store);
  assertVideoSkipSources(ledger.episodes, manifest.episodes);
  let ctx: RunContext = store.has("context.json") ? store.readJson<RunContext>("context.json") : emptyContext();
  const existing = ledger.episodes.find((record) => record.episode === episode);
  if (existing) {
    const currentManifest = migrateVideoManifest(store, manifest);
    ctx = reconcileVideoSkips(store, ctx, ledger.episodes);
    const identityMap = store.has("v4_identity_map.json")
      ? store.readJson<V4IdentityMap>("v4_identity_map.json")
      : emptyV4IdentityMap();
    publishFinalizedVideoWorkspace(store, currentManifest.episodes, ctx, deriveVideoTitle(currentManifest.source.path), identityMap, ledger.episodes);
    return false;
  }

  const nextEpisode = ctx.lastEpisode + 1;
  if (episode !== nextEpisode) {
    throw inputError(
      "Only the next unresolved video episode can be skipped.",
      "INGEST_SKIP_NOT_NEXT",
      "Read status.next_episode and skip exactly that episode.",
      [`requested=${episode}`, `next_episode=${nextEpisode}`],
    );
  }
  const episodeRel = `ep/ep_${String(episode).padStart(3, "0")}`;
  if (store.has(`${episodeRel}.json`)) {
    throw inputError("A parsed episode cannot be skipped.", "INGEST_SKIP_ALREADY_PARSED", "Continue ingest from the next unresolved episode.", [`episode=${episode}`]);
  }
  if (!store.has(`${episodeRel}.error.json`)) {
    throw inputError(
      "The episode has no recorded ingest failure.",
      "INGEST_SKIP_FAILURE_REQUIRED",
      "Run ingest for this episode first; a typed error sidecar is required before manual skip.",
      [`episode=${episode}`],
    );
  }
  const sidecar = parseIngestErrorSidecar(store.readJson(`${episodeRel}.error.json`));
  if (!sidecar || sidecar.unit.episode !== episode) {
    throw inputError(
      "The episode error sidecar is invalid.",
      "INGEST_SKIP_FAILURE_INVALID",
      "Regenerate the typed failure by rerunning video ingest.",
      [`${episodeRel}.error.json`],
    );
  }

  const record: VideoSkipRecord = {
    episode,
    skipped_at: new Date().toISOString(),
    reason,
    source_sha256: sourceEpisode.sha256,
    failure: sidecar.failure,
    attempts: sidecar.attempts,
  };
  const currentManifest = migrateVideoManifest(store, manifest);
  const updatedSkips = [...ledger.episodes, record];
  store.writeJson("manifest.json", {
    ...currentManifest,
    updated: record.skipped_at,
    current_error: undefined,
    skips: updatedSkips,
  });
  writeVideoSkipLedger(store, { schema_version: 1, episodes: updatedSkips });
  ctx = reconcileVideoSkips(store, ctx, updatedSkips);
  const identityMap = store.has("v4_identity_map.json")
    ? store.readJson<V4IdentityMap>("v4_identity_map.json")
    : emptyV4IdentityMap();
  publishFinalizedVideoWorkspace(
    store,
    currentManifest.episodes,
    ctx,
    deriveVideoTitle(currentManifest.source.path),
    identityMap,
    [...ledger.episodes, record],
  );
  return true;
}

export async function commandSkipVideoEpisode(opts: Dict): Promise<[CommandOutput, number]> {
  const workspacePath = path.resolve(typeof opts["workspace_path"] === "string" ? opts["workspace_path"] : "workspace");
  const episode = episodeOf(opts["episode"]);
  const reason = typeof opts["reason"] === "string" ? opts["reason"].trim() : "";
  if (!reason) {
    throw inputError("--reason is required.", "INGEST_SKIP_REASON_REQUIRED", "Record why this episode cannot be parsed.", ["--reason <text>"]);
  }
  const changed = await withIngestWorkspaceLock(workspacePath, () => skipVideoEpisodeUnlocked(workspacePath, episode, reason));
  const status = deriveIngestStatus(workspacePath);
  const skippedEpisodes = readVideoSkipLedger(new WorkspaceStore(workspacePath)).episodes.map((record) => record.episode);
  return [{
    text: changed
      ? `Episode ${episode} skipped; ingest can continue at episode ${status.next_episode ?? "end"}.`
      : `Episode ${episode} was already skipped; no changes made.`,
    data: {
      workspace: workspacePath,
      episode,
      changed,
      skipped_episodes: skippedEpisodes,
      complete: status.complete ?? false,
      next_episode: status.next_episode ?? null,
      script_path: path.join(workspacePath, "script.json"),
    },
  }, EXIT_OK];
}
